/**
 * WaterRenderPass.ts — projected-grid water surface, two FFT cascades.
 *
 * Reads:  Camera, Lighting, Palette, Water (uniform), Terrain (uniform),
 *         heightmap texture, and 6 cascade output textures (h, dx, dz × 2).
 * Writes: RenderTarget.color (load), RenderTarget.depth (load, reverse-Z).
 *
 * Geometry: 257×257 vertices = 256×256 quads, drawn as indexed triangles. The
 * vertex shader unprojects each NDC grid point to a ray and intersects it with
 * the water plane, then displaces by the FFT cascade outputs. Vertices whose
 * ray misses the water plane in front of the camera are pushed to a far ring
 * at water level so the geometry stays well-formed.
 */

import waterWgsl from "../shaders/water.wgsl?raw";
import type { Camera } from "../Camera";
import type { LightingUniforms } from "../rendering/LightingUniforms";
import type { PaletteUniforms } from "../rendering/PaletteUniforms";
import type { ManagedRenderTarget } from "../rendering/RenderTarget";
import { REVERSE_Z_COMPARE } from "../rendering/RenderTarget";
import type { FrameContext, RenderPass } from "../rendering/RenderPass";
import type { TerrainRenderPass } from "./TerrainRenderPass";
import { OceanSimulation } from "./ocean/OceanSimulation";
import type { FetchPass } from "./FetchPass";
import type { SWEPass } from "./SWEPass";
import type { WindFieldPass } from "./WindFieldPass";
import type { SkyCubemapPass } from "./SkyCubemapPass";
import type { RippleBufferPass } from "./RippleBufferPass";
import type { WakeTrailPass } from "./WakeTrailPass";
import type { Boat } from "../sim/Boat";
import {
  getBoatCollisionLayers,
  type BoatCollisionProfile,
} from "../sim/Collisions";
import {
  createBufferFromTypedArray,
  createUniformBuffer,
} from "../gpu/Buffers";

const GRID_VPS = 129;
const GRID_QUADS = GRID_VPS - 1;

/** Max boats whose hull footprint suppresses water-surface elevation. Must
 *  match `MAX_HULL_SUPPRESS` in water.wgsl. */
const MAX_HULL_SUPPRESS = 4;
/** Floats per boat in the suppression list (5 vec4 = 20 floats). */
const HULL_SUPPRESS_FLOATS = 20;
const HULL_PROFILE_SAMPLES = 8;
/** Header (20 floats = 80 B) + boatCount(1)+pad(3) + 4 boats × 20 floats. */
const WATER_UNIFORM_SIZE =
  80 + 16 + MAX_HULL_SUPPRESS * HULL_SUPPRESS_FLOATS * 4;

function sampleCollisionHalfBeam(
  profile: BoatCollisionProfile,
  x: number,
): number {
  const outline = profile.outline;
  if (outline.length === 0) return profile.halfBeam;
  let bestDx = Infinity;
  let best = 0;
  for (const p of outline) {
    const dx = Math.abs(p.x - x);
    if (dx < bestDx) {
      bestDx = dx;
      best = Math.abs(p.z);
    } else if (dx === bestDx) {
      best = Math.max(best, Math.abs(p.z));
    }
  }
  return best;
}

export class WaterRenderPass implements RenderPass {
  readonly name = "WaterRenderPass";

  private pipeline: GPURenderPipeline;
  private bgl: GPUBindGroupLayout;
  /** Bind groups indexed by sweB*4 + ripB*2 + wakeB. */
  private bindGroups: GPUBindGroup[];
  private swePass: SWEPass;
  private ripplePass: RippleBufferPass;
  private wakeTrailPass: WakeTrailPass;
  private boats: Boat[];
  private indexBuffer: GPUBuffer;
  private indexCount: number;
  private waterUniform: GPUBuffer;
  private waterUniformCpu = new Float32Array(WATER_UNIFORM_SIZE / 4);
  private terrainUniform: GPUBuffer;

  /** Set per-frame from AppState. */

  /** Shadow bind group (group 1). Set externally before encode(). */
  shadowBindGroup: GPUBindGroup | null = null;

  constructor(
    device: GPUDevice,
    rt: ManagedRenderTarget,
    camera: Camera,
    lighting: LightingUniforms,
    palette: PaletteUniforms,
    terrainPass: TerrainRenderPass,
    private ocean: OceanSimulation,
    shadowConsumerLayout: GPUBindGroupLayout,
    fetchPass: FetchPass,
    swePass: SWEPass,
    windFieldPass: WindFieldPass,
    skyCubemapPass: SkyCubemapPass,
    ripplePass: RippleBufferPass,
    wakeTrailPass: WakeTrailPass,
    boats: Boat[],
  ) {
    this.swePass = swePass;
    this.ripplePass = ripplePass;
    this.wakeTrailPass = wakeTrailPass;
    this.boats = boats;
    // Index buffer for the projected-grid quads.
    const indices = new Uint32Array(GRID_QUADS * GRID_QUADS * 6);
    let k = 0;
    for (let j = 0; j < GRID_QUADS; j++) {
      for (let i = 0; i < GRID_QUADS; i++) {
        const a = j * GRID_VPS + i;
        const b = a + 1;
        const c = a + GRID_VPS;
        const d = c + 1;
        indices[k++] = a;
        indices[k++] = c;
        indices[k++] = b;
        indices[k++] = b;
        indices[k++] = c;
        indices[k++] = d;
      }
    }
    this.indexCount = indices.length;
    this.indexBuffer = createBufferFromTypedArray(
      device,
      indices,
      GPUBufferUsage.INDEX | GPUBufferUsage.COPY_DST,
      "WaterRenderPass.index",
    );

    this.waterUniform = createUniformBuffer(
      device,
      WATER_UNIFORM_SIZE,
      "WaterRenderPass.water",
    );
    // Terrain uniform: extent, res, _pad, _pad
    this.terrainUniform = createUniformBuffer(
      device,
      16,
      "WaterRenderPass.terrain",
    );
    device.queue.writeBuffer(
      this.terrainUniform,
      0,
      new Float32Array([
        terrainPass.heightmap.extent,
        terrainPass.heightmap.resolution,
        0,
        0,
      ]),
    );

    const module = device.createShaderModule({
      label: "water.wgsl",
      code: waterWgsl,
    });
    this.bgl = device.createBindGroupLayout({
      label: "WaterRenderPass.bgl",
      entries: [
        {
          binding: 0,
          visibility: GPUShaderStage.VERTEX | GPUShaderStage.FRAGMENT,
          buffer: { type: "uniform" },
        },
        {
          binding: 1,
          visibility: GPUShaderStage.FRAGMENT,
          buffer: { type: "uniform" },
        },
        {
          binding: 2,
          visibility: GPUShaderStage.FRAGMENT,
          buffer: { type: "uniform" },
        },
        {
          binding: 3,
          visibility: GPUShaderStage.VERTEX | GPUShaderStage.FRAGMENT,
          buffer: { type: "uniform" },
        },
        {
          binding: 4,
          visibility: GPUShaderStage.VERTEX | GPUShaderStage.FRAGMENT,
          buffer: { type: "uniform" },
        },
        {
          binding: 5,
          visibility: GPUShaderStage.VERTEX | GPUShaderStage.FRAGMENT,
          texture: { sampleType: "unfilterable-float" },
        },
        {
          binding: 6,
          visibility: GPUShaderStage.VERTEX | GPUShaderStage.FRAGMENT,
          texture: { sampleType: "unfilterable-float" },
        },
        {
          binding: 7,
          visibility: GPUShaderStage.VERTEX,
          texture: { sampleType: "unfilterable-float" },
        },
        {
          binding: 8,
          visibility: GPUShaderStage.VERTEX,
          texture: { sampleType: "unfilterable-float" },
        },
        {
          binding: 9,
          visibility: GPUShaderStage.VERTEX | GPUShaderStage.FRAGMENT,
          texture: { sampleType: "unfilterable-float" },
        },
        {
          binding: 10,
          visibility: GPUShaderStage.VERTEX | GPUShaderStage.FRAGMENT,
          texture: { sampleType: "unfilterable-float" },
        },
        {
          binding: 11,
          visibility: GPUShaderStage.VERTEX | GPUShaderStage.FRAGMENT,
          texture: { sampleType: "unfilterable-float" },
        },
        {
          binding: 12,
          visibility: GPUShaderStage.VERTEX | GPUShaderStage.FRAGMENT,
          texture: { sampleType: "unfilterable-float" },
        },
        {
          binding: 13,
          visibility: GPUShaderStage.VERTEX | GPUShaderStage.FRAGMENT,
          texture: { sampleType: "unfilterable-float" },
        },
        {
          binding: 14,
          visibility: GPUShaderStage.VERTEX | GPUShaderStage.FRAGMENT,
          texture: { sampleType: "unfilterable-float" },
        },
        {
          binding: 15,
          visibility: GPUShaderStage.FRAGMENT,
          texture: { sampleType: "float", viewDimension: "cube" },
        },
        {
          binding: 16,
          visibility: GPUShaderStage.FRAGMENT,
          sampler: { type: "filtering" },
        },
        {
          binding: 17,
          visibility: GPUShaderStage.VERTEX | GPUShaderStage.FRAGMENT,
          texture: { sampleType: "unfilterable-float" },
        },
        {
          binding: 18,
          visibility: GPUShaderStage.FRAGMENT,
          texture: { sampleType: "unfilterable-float" },
        },
        {
          binding: 19,
          visibility: GPUShaderStage.FRAGMENT,
          texture: { sampleType: "uint" },
        },
        {
          binding: 20,
          visibility: GPUShaderStage.FRAGMENT,
          texture: { sampleType: "unfilterable-float" },
        },
      ],
    });
    this.pipeline = device.createRenderPipeline({
      label: "WaterRenderPass.pipeline",
      layout: device.createPipelineLayout({
        bindGroupLayouts: [this.bgl, shadowConsumerLayout],
      }),
      vertex: { module, entryPoint: "vs_main" },
      fragment: {
        module,
        entryPoint: "fs_main",
        targets: [
          {
            format: rt.current.colorFormat,
            blend: {
              // Soft shoreline: water fragments with low alpha blend over land.
              color: {
                srcFactor: "src-alpha",
                dstFactor: "one-minus-src-alpha",
                operation: "add",
              },
              alpha: {
                srcFactor: "one",
                dstFactor: "one-minus-src-alpha",
                operation: "add",
              },
            },
          },
        ],
      },
      primitive: { topology: "triangle-list", cullMode: "none" },
      depthStencil: {
        format: rt.current.depthFormat,
        depthWriteEnabled: true,
        depthCompare: REVERSE_Z_COMPARE,
      },
    });

    const v1 = ocean.cascade1.views();
    const v2 = ocean.cascade2.views();
    const sweViews = swePass.views();
    const baseEntries = [
      { binding: 0, resource: { buffer: camera.buffer } },
      { binding: 1, resource: { buffer: lighting.buffer } },
      { binding: 2, resource: { buffer: palette.buffer } },
      { binding: 3, resource: { buffer: this.waterUniform } },
      { binding: 4, resource: { buffer: this.terrainUniform } },
      { binding: 5, resource: terrainPass.heightmapView() },
      { binding: 6, resource: v1.hOut },
      { binding: 7, resource: v1.dxOut },
      { binding: 8, resource: v1.dzOut },
      { binding: 9, resource: v2.hOut },
      { binding: 10, resource: v2.dxOut },
      { binding: 11, resource: v2.dzOut },
      { binding: 12, resource: fetchPass.view },
      { binding: 14, resource: windFieldPass.view },
      { binding: 15, resource: skyCubemapPass.cubeView },
      {
        binding: 16,
        resource: device.createSampler({
          label: "WaterRenderPass.skySampler",
          magFilter: "linear",
          minFilter: "linear",
        }),
      },
      { binding: 19, resource: terrainPass.classificationView() },
      { binding: 20, resource: terrainPass.polygonFadeView() },
    ] satisfies GPUBindGroupEntry[];
    this.bindGroups = [];
    const ripViews = ripplePass.views();
    const wakeViews = wakeTrailPass.views();
    for (let s = 0; s < 2; s++) {
      for (let r = 0; r < 2; r++) {
        for (let w = 0; w < 2; w++) {
          this.bindGroups.push(
            device.createBindGroup({
              label: `WaterRenderPass.bg.swe${s}.rip${r}.wake${w}`,
              layout: this.bgl,
              entries: [
                ...baseEntries,
                { binding: 13, resource: s === 0 ? sweViews.a : sweViews.b },
                { binding: 17, resource: r === 0 ? ripViews.a : ripViews.b },
                { binding: 18, resource: w === 0 ? wakeViews.a : wakeViews.b },
              ],
            }),
          );
        }
      }
    }
  }

  encode(encoder: GPUCommandEncoder, frame: FrameContext): void {
    const s = frame.state;
    // Wind direction: meteorological azimuth (degrees from). Convert to
    // a unit vector pointing where wind blows TO in world XZ.
    const blowToDeg = (s.windDirectionDeg + 180) % 360;
    const blowToRad = (blowToDeg * Math.PI) / 180;
    const wdx = Math.sin(blowToRad);
    const wdz = -Math.cos(blowToRad);
    // Water uniform must be re-written every frame because the ripple-texture
    // and wake-trail centres move with the player boat.
    const data = this.waterUniformCpu;
    data.fill(0);
    data[0] = s.waterLevel;
    data[1] = this.ocean.cascade1.config.L;
    data[2] = this.ocean.cascade2.config.L;
    data[3] = s.choppiness;
    data[4] = 120.0; // blendNearDist
    data[5] = 500.0; // blendFarDist
    data[6] = 0.0; // rippleAmp — surface displacement off (Mach-cone wake from
    // the linear wave eq. read as spreading circles, not Kelvin V).
    data[7] = this.ripplePass.extent;
    data[8] = this.ripplePass.centerXZ[0];
    data[9] = this.ripplePass.centerXZ[1];
    data[10] = wdx;
    data[11] = wdz;
    data[12] = s.windSpeed;
    data[13] = frame.time;
    data[14] = this.wakeTrailPass.centerXZ[0];
    data[15] = this.wakeTrailPass.centerXZ[1];
    data[16] = this.wakeTrailPass.extent;
    data[17] = 1.0; // wakeAmp
    // data[18..19] = pad

    // Hull-suppression block: keeps wave elevation from rising above each
    // boat's centreline (posY) inside the hull footprint, so even big waves
    // never flood the deck/cockpit visually.
    //   data[20]   : boatCount (u32 reinterpreted as f32 below)
    //   data[24..] : per-boat 5× vec4 = (cx, cz, halfLen, halfBeam),
    //                                    (cosH, sinH, maxY, speed),
    //                                    (xMin, xMax, maxHalfBeam, hasProfile),
    //                                    halfBeam profile samples 0..3,
    //                                    halfBeam profile samples 4..7
    const boatCount = Math.min(this.boats.length, MAX_HULL_SUPPRESS);
    new Uint32Array(data.buffer, data.byteOffset, data.length)[20] = boatCount;
    const base = 24; // first per-boat slot, 16-byte aligned
    for (let i = 0; i < boatCount; i++) {
      const b = this.boats[i]!;
      const p = b.params;
      // Half-extents inflated slightly so the falloff feathers nicely without
      // exposing a flat ring around the hull.
      const halfLen = p.length * 0.55;
      const halfBeam = p.beam * 0.65;
      const o = base + i * HULL_SUPPRESS_FLOATS;
      data[o + 0] = b.posX;
      data[o + 1] = b.posZ;
      data[o + 2] = halfLen;
      data[o + 3] = halfBeam;
      data[o + 4] = Math.cos(b.heading);
      data[o + 5] = Math.sin(b.heading);
      // Cap water elevation at hull centreline minus a small safety margin so
      // the surface stays clearly below the deck even with wake/seiche on top.
      data[o + 6] = b.posY - 0.05;
      // Boat speed (m/s) — magnitude of body-frame surge/sway. Drives the
      // amplitude of the analytical hull-wake displacement in the water shader.
      data[o + 7] = Math.hypot(b.surge, b.sway);
      const waterlineProfile = getBoatCollisionLayers(b)[0]?.profile;
      if (waterlineProfile) {
        data[o + 8] = waterlineProfile.xMin;
        data[o + 9] = waterlineProfile.xMax;
        data[o + 10] = waterlineProfile.halfBeam;
        data[o + 11] = 1;
        const span = Math.max(
          waterlineProfile.xMax - waterlineProfile.xMin,
          1e-4,
        );
        for (let sIdx = 0; sIdx < HULL_PROFILE_SAMPLES; sIdx++) {
          const x =
            waterlineProfile.xMin + (span * sIdx) / (HULL_PROFILE_SAMPLES - 1);
          data[o + 12 + sIdx] = sampleCollisionHalfBeam(waterlineProfile, x);
        }
      } else {
        data[o + 8] = -halfLen;
        data[o + 9] = halfLen;
        data[o + 10] = halfBeam;
        data[o + 11] = 0;
        for (let sIdx = 0; sIdx < HULL_PROFILE_SAMPLES; sIdx++) {
          const t = sIdx / (HULL_PROFILE_SAMPLES - 1);
          data[o + 12 + sIdx] = halfBeam * Math.sin(Math.PI * t);
        }
      }
    }
    frame.device.queue.writeBuffer(this.waterUniform, 0, data);

    const pass = encoder.beginRenderPass({
      label: "WaterRenderPass",
      colorAttachments: [
        {
          view: frame.renderTarget.colorView,
          loadOp: "load",
          storeOp: "store",
        },
      ],
      depthStencilAttachment: {
        view: frame.renderTarget.depthView,
        depthLoadOp: "load",
        depthStoreOp: "store",
      },
    });
    pass.setPipeline(this.pipeline);
    // SWEPass, RippleBufferPass and WakeTrailPass run BEFORE this; their
    // currentIsViewB flags identify the most-recently-written textures.
    const sweIdx = this.swePass.currentIsViewB ? 1 : 0;
    const ripIdx = this.ripplePass.currentIsViewB ? 1 : 0;
    const wakeIdx = this.wakeTrailPass.currentIsViewB ? 1 : 0;
    pass.setBindGroup(0, this.bindGroups[sweIdx * 4 + ripIdx * 2 + wakeIdx]!);
    if (this.shadowBindGroup !== null) {
      pass.setBindGroup(1, this.shadowBindGroup);
    }
    pass.setIndexBuffer(this.indexBuffer, "uint32");
    pass.drawIndexed(this.indexCount, 1, 0, 0, 0);
    pass.end();
  }

  destroy(): void {
    this.indexBuffer.destroy();
    this.waterUniform.destroy();
    this.terrainUniform.destroy();
  }
}
